import { createApiCall } from './index'

export type MarketSourceKind = 'rss' | 'sitemap' | 'social' | 'other'
export type MarketSourceCategory = 'market' | 'regulatory'

/** VRG-741 — a competitor a feed can belong to. */
export interface IMarketSourceCompetitor {
  domain: string
  name: string | null
  kind: string
  feedCount: number
  /** VRG-741 — the automatic feed finder's last check (null = never checked). */
  feedsCheckedAt: string | null
  feedsCheckOutcome: 'found' | 'none' | 'error' | null
  feedsCheckNote: string | null
}

/** VRG-741 — one competitor's feed check ("Re-scan feeds"). */
export interface ICompetitorFeedCheck {
  domain: string
  outcome: 'found' | 'none' | 'error' | 'skipped'
  skippedReason?: string
  added: { id: string; url: string; kind: string }[]
  existing: string[]
  note: string
  checkedAt: string | null
  requests: number
}

/** VRG-741 — what feed auto-detection found on a domain (nothing is saved). */
export interface IFeedDetectResult {
  domain: string
  candidates: { url: string; kind: 'rss' | 'sitemap'; title: string | null; via: 'link_rel' | 'robots' | 'common_path' }[]
  tried: { url: string; outcome: string }[]
}

export interface IMarketSource {
  id: string
  name: string
  url: string
  kind: MarketSourceKind
  notes: string | null
  enabled: boolean
  cadenceMinutes: number
  /** Extract stats automatically on ingest. Off by default — see the Sources table. */
  autoExtract: boolean
  lastPolledAt: string | null
  /** Current streak — reset to 0 by any successful sweep, not a lifetime total. */
  consecutiveFailures: number
  lastError: string | null
  lastErrorAt: string | null
  /** Items attributed to this source. Distinguishes "quiet" from "erroring" (AC8). */
  itemCount: number
  /** VRG-741 — the competitor this feed belongs to (seo_competitors.domain), or null. */
  competitorDomain: string | null
  competitorName: string | null
  /** VRG-741 — what the source publishes: regulatory items become Regulation ideas. */
  category: MarketSourceCategory
  /** VRG-741 — `auto_discovered` = added by the competitor feed finder ("Found automatically"). */
  origin: 'manual' | 'auto_discovered'
  /** VRG-741 — a sitemap's kept path prefixes (blog / news / insights); null = every link. */
  includePaths: string[] | null
  createdAt: string
  updatedAt: string
}

export interface IMarketFetchLogEntry {
  id: string
  url: string
  outcome: 'ingested' | 'duplicate' | 'rejected'
  reason: string | null
  detail: string | null
  createdAt: string
}

export interface SweepOutcome {
  sourcesPolled: number
  itemsIngested: number
  itemsDuplicate: number
  itemsRejected: number
  sourcesFailed: number
}

export interface CreateMarketSourcePayload {
  name: string
  url: string
  kind: MarketSourceKind
  notes?: string | null
  enabled?: boolean
  cadenceMinutes?: number
  autoExtract?: boolean
  competitorDomain?: string | null
  category?: MarketSourceCategory
}

export type UpdateMarketSourcePayload = Partial<Omit<CreateMarketSourcePayload, 'url'>>

/**
 * Market-source registry API client (VRG-462).
 *
 * Same `ok()` guard as `marketItemsApi` beside it, for the same non-optional reason:
 * `createApiCall`'s errorHandler RESOLVES with the error response rather than
 * throwing on non-401 failures, so an unchecked 403 would resolve as data and the
 * page would render "no sources" to someone who simply cannot see them.
 */
export class MarketSourcesApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'MarketSourcesApiError'
  }
}

// Named, not `isForbidden` — supportApi and announcementsApi already export that
// name into the `services` barrel and a third would add a second TS2308 ambiguity
// rather than fix the first. See VRG-498.
export const isMarketSourcesForbidden = (error: unknown): boolean =>
  error instanceof MarketSourcesApiError && error.status === 403

const ok = (response: any, fallback: string) => {
  if (response.status !== 200 && response.status !== 201) {
    throw new MarketSourcesApiError(response?.data?.message || fallback, response?.status ?? 0)
  }
  return response.data
}

export const marketSourcesApi = {
  async list(): Promise<IMarketSource[]> {
    const response = await createApiCall({ method: 'GET', url: '/internal/market-sources' })
    return ok(response, 'Failed to load market sources')
  },

  async log(id: string): Promise<IMarketFetchLogEntry[]> {
    const response = await createApiCall({ method: 'GET', url: `/internal/market-sources/${id}/log` })
    return ok(response, "Couldn't load that source's history")
  },

  async create(payload: CreateMarketSourcePayload): Promise<IMarketSource> {
    const response = await createApiCall({
      method: 'POST',
      url: '/internal/market-sources',
      data: payload,
    })
    return ok(response, 'Could not add that source')
  },

  async update(id: string, payload: UpdateMarketSourcePayload): Promise<IMarketSource> {
    const response = await createApiCall({
      method: 'PATCH',
      url: `/internal/market-sources/${id}`,
      data: payload,
    })
    return ok(response, "Couldn't update that source")
  },

  async pollNow(id: string): Promise<SweepOutcome> {
    const response = await createApiCall({
      method: 'POST',
      url: `/internal/market-sources/${id}/poll`,
    })
    return ok(response, "Couldn't poll that source")
  },

  async competitors(): Promise<IMarketSourceCompetitor[]> {
    const response = await createApiCall({ method: 'GET', url: '/internal/market-sources/competitors' })
    return ok(response, "Couldn't load the competitors")
  },

  async detect(domain: string): Promise<IFeedDetectResult> {
    const response = await createApiCall({ method: 'POST', url: '/internal/market-sources/detect', data: { domain } })
    return ok(response, "Couldn't look for a feed on that site")
  },

  /** VRG-741 — find and add one competitor's blog feeds now (ignores the weekly limit). */
  async rescanCompetitor(domain: string): Promise<ICompetitorFeedCheck> {
    const response = await createApiCall({
      method: 'POST',
      url: `/internal/market-sources/competitors/${encodeURIComponent(domain)}/rescan`,
    })
    return ok(response, "Couldn't scan that competitor for feeds")
  },

  /** VRG-741 — scan every due competitor in the background. */
  async rescanAll(): Promise<{ queued: number; alreadyRunning: boolean }> {
    const response = await createApiCall({ method: 'POST', url: '/internal/market-sources/rescan' })
    return ok(response, "Couldn't start the feed scan")
  },

  async remove(id: string): Promise<{ deleted: true }> {
    const response = await createApiCall({
      method: 'DELETE',
      url: `/internal/market-sources/${id}`,
    })
    return ok(response, "Couldn't delete that source")
  },
}
